import ClosingNotice from './ClosingNotice';
import React, { useState, useEffect } from 'react';
import { PushPermissionPrompt, InstallPrompt } from './PWAPrompts';
import ReportPage from './ReportPage';
import SymptomCalendar from './SymptomCalendar';
import WaitlistModal from './WaitlistModal';
import JourneyStageModal from './JourneyStageModal';

// ─── Font constants ──────────────────────────────────────────────────────────
const sans = "'DM Sans', system-ui, sans-serif";
const serif = "'DM Serif Display', Georgia, serif";

// ─── Symptom definitions — journey-stage prioritised ───────────────────────
const ALL_SYMPTOMS = [
  // Core — shown first for all users
  { key: 'hot_flashes',       label: 'Hot flashes',                  group: 'core' },
  { key: 'night_sweats',      label: 'Night sweats',                 group: 'core' },
  { key: 'sleep_issues',      label: 'Sleep issues',                 group: 'core' },
  { key: 'fatigue',           label: 'Fatigue / low energy',         group: 'core' },
  { key: 'brain_fog',         label: 'Brain fog / poor concentration', group: 'core' },
  { key: 'mood_irritability', label: 'Mood swings / irritability',   group: 'core' },
  { key: 'anxiety',           label: 'Anxiety',                      group: 'core' },
  { key: 'low_mood',          label: 'Low mood / depression',        group: 'core' },
  // Physical
  { key: 'joint_pain',        label: 'Joint pain / aches',           group: 'physical' },
  { key: 'headaches',         label: 'Headaches / migraines',        group: 'physical' },
  { key: 'heart_palpitations',label: 'Heart palpitations',           group: 'physical' },
  { key: 'dizziness',         label: 'Dizziness / lightheadedness',  group: 'physical' },
  { key: 'bloating',          label: 'Bloating / digestive changes', group: 'physical' },
  { key: 'weight_changes',    label: 'Weight changes / meno belly',  group: 'physical' },
  { key: 'breast_tenderness', label: 'Breast tenderness',            group: 'physical' },
  { key: 'skin_changes',      label: 'Skin changes / itching',       group: 'physical' },
  { key: 'hair_thinning',     label: 'Hair thinning',                group: 'physical' },
  // Genitourinary
  { key: 'vaginal_dryness',   label: 'Vaginal dryness',              group: 'genit' },
  { key: 'frequent_urination',label: 'Frequent urination / urgency', group: 'genit' },
  { key: 'recurrent_utis',    label: 'Recurrent UTIs',               group: 'genit' },
  { key: 'low_libido',        label: 'Low sex drive / libido',       group: 'genit' },
  { key: 'pain_during_sex',   label: 'Pain during sex',              group: 'genit' },
  { key: 'sensitivity_changes',label: 'Changes in sensation / sensitivity', group: 'genit' },
  // Cycle
  { key: 'period_changes',    label: 'Period changes / irregular bleeding', group: 'cycle' },
  { key: 'heavy_periods',     label: 'Heavy periods',                        group: 'cycle' },
  // Neurological / other
  { key: 'tinnitus',          label: 'Tinnitus / ringing ears',              group: 'other' },
  { key: 'panic_attacks',     label: 'Panic attacks',                        group: 'other' },
];

// Journey-stage symptom priority — keys that bubble to top
const JOURNEY_PRIORITY = {
  started_hrt:      ['hot_flashes', 'night_sweats', 'mood_irritability', 'sleep_issues', 'fatigue', 'brain_fog', 'anxiety', 'heart_palpitations'],
  adjusting_hrt:    ['hot_flashes', 'night_sweats', 'mood_irritability', 'fatigue', 'sleep_issues', 'anxiety', 'brain_fog', 'heart_palpitations'],
  seeking_diagnosis:['mood_irritability', 'fatigue', 'brain_fog', 'anxiety', 'sleep_issues', 'hot_flashes', 'night_sweats', 'period_changes'],
  noticing_symptoms:['period_changes', 'mood_irritability', 'fatigue', 'sleep_issues', 'hot_flashes', 'brain_fog', 'anxiety', 'low_mood'],
  preparing_appointment: ['hot_flashes', 'night_sweats', 'fatigue', 'mood_irritability', 'sleep_issues', 'brain_fog', 'anxiety', 'low_mood'],
  managing_symptoms:['fatigue', 'sleep_issues', 'mood_irritability', 'brain_fog', 'hot_flashes', 'night_sweats', 'anxiety', 'low_mood'],
};

function getSortedSymptoms(journeyStage, yesterdaySymptoms) {
  const priority = JOURNEY_PRIORITY[journeyStage] || [];
  const yesterdayKeys = yesterdaySymptoms ? Object.keys(yesterdaySymptoms) : [];

  // yesterdayKeys first, then journey priority, then rest
  const seen = new Set();
  const ordered = [];

  // 1. Yesterday's symptoms first
  for (const key of yesterdayKeys) {
    const s = ALL_SYMPTOMS.find(s => s.key === key);
    if (s && !seen.has(key)) { ordered.push({ ...s, wasYesterday: true }); seen.add(key); }
  }

  // 2. Journey priority
  for (const key of priority) {
    const s = ALL_SYMPTOMS.find(s => s.key === key);
    if (s && !seen.has(key)) { ordered.push(s); seen.add(key); }
  }

  // 3. Rest
  for (const s of ALL_SYMPTOMS) {
    if (!seen.has(s.key)) ordered.push(s);
  }

  return ordered;
}

// ─── AI label ──────────────────────────────────────────────────────────────
// EU AI Act Art. 50: tell people at first contact that text is AI-written.
// Shown only on text the model actually wrote, not on fallback messages.
function AiLabel({ centered = false }) {
  return (
    <div style={{ ...stepStyles.aiLabel, ...(centered ? { justifyContent: 'center' } : {}) }}>
      <span aria-hidden="true">✦</span> Written by AI from your logs
    </div>
  );
}

// ─── Step 1: AI Greeting ─────────────────────────────────────────────────
function GreetingStep({ greeting, greetingIsAI, loading, onNext, totalCount, todayLogged, onEditYesterday }) {
  // Already logged today — show a calm done state instead of the greeting
  if (todayLogged && !loading) {
    return (
      <div style={stepStyles.container}>
        <div style={stepStyles.stepBadge}>Day {totalCount}</div>
        <p style={stepStyles.greetingText}>
          Today is logged. Come back tomorrow and keep the streak going.
        </p>
        <div style={stepStyles.alreadyLoggedBanner}>
          Today's entry is saved — tap below if you want to update it.
        </div>
        <button
          style={{ ...stepStyles.primaryBtn, marginTop: '24px' }}
          onClick={onNext}
        >
          Update today's log →
        </button>
        {onEditYesterday && (
          <button
            style={{
              background: 'none', border: '1.5px solid rgba(44,44,42,0.12)', cursor: 'pointer',
              color: '#4a6070', fontSize: '14px', marginTop: '10px',
              fontFamily: sans, display: 'block', width: '100%',
              textAlign: 'center', borderRadius: '10px', padding: '11px 0',
              fontWeight: '500',
            }}
            onClick={onEditYesterday}
          >
            Edit yesterday's log
          </button>
        )}
      </div>
    );
  }

  return (
    <div style={stepStyles.container}>
      <div style={stepStyles.stepBadge}>
        {totalCount === 0 ? 'Day 1' : `Day ${totalCount + 1}`}
      </div>
      {loading ? (
        <div style={stepStyles.greetingLoading}>
          <div style={stepStyles.loadingDot} />
          <div style={{ ...stepStyles.loadingDot, animationDelay: '0.15s' }} />
          <div style={{ ...stepStyles.loadingDot, animationDelay: '0.3s' }} />
        </div>
      ) : (
        <>
          <p style={stepStyles.greetingText}>{greeting}</p>
          {greetingIsAI && <AiLabel />}
        </>
      )}
            <button
        style={{ ...stepStyles.primaryBtn, marginTop: '32px' }}
        onClick={onNext}
        disabled={loading}
      >
        {totalCount === 0 ? "Let's start →" : "Log today →"}
      </button>
      {!loading && onEditYesterday && (
        <button
          style={{
            background: 'none', border: '1.5px solid #d0e4d8', cursor: 'pointer',
            color: '#7A9E87', fontSize: '14px', marginTop: '12px',
            fontFamily: 'inherit', display: 'block', width: '100%',
            textAlign: 'center', borderRadius: '10px', padding: '11px 0',
            fontWeight: '600', letterSpacing: '0.01em',
          }}
          onClick={onEditYesterday}
        >
          Edit yesterday's log
        </button>
      )}
    </div>
  );
}

// ─── Step 2: Mood check ─────────────────────────────────────────────────────
const MOOD_OPTIONS = [
  { value: 1, emoji: '😞', label: 'Terrible' },
  { value: 3, emoji: '😕', label: 'Rough' },
  { value: 5, emoji: '😐', label: 'Okay' },
  { value: 7, emoji: '🙂', label: 'Good' },
  { value: 9, emoji: '😊', label: 'Great' },
];

function MoodStep({ value, onChange, onNext, onBack }) {
  return (
    <div style={stepStyles.container}>
      <button style={stepStyles.backBtn} onClick={onBack}>← Back</button>
      <h2 style={stepStyles.stepTitle}>How are you feeling today?</h2>
      <p style={stepStyles.stepSubtitle}>Just your overall sense of today.</p>
      <div style={stepStyles.moodGrid}>
        {MOOD_OPTIONS.map(opt => (
          <button
            key={opt.value}
            style={{
              ...stepStyles.moodBtn,
              ...(value === opt.value ? stepStyles.moodBtnActive : {}),
            }}
            onClick={() => { onChange(opt.value); setTimeout(onNext, 200); }}
          >
            <span style={stepStyles.moodEmoji}>{opt.emoji}</span>
            <span style={stepStyles.moodLabel}>{opt.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Step 3: Symptoms ───────────────────────────────────────────────────────
function SymptomsStep({ symptoms, onChange, onNext, onBack, journeyStage, yesterdaySymptoms }) {
  const sorted = getSortedSymptoms(journeyStage, yesterdaySymptoms);
  const yesterdayKeys = yesterdaySymptoms ? Object.keys(yesterdaySymptoms) : [];
  const [showAll, setShowAll] = useState(false);
  const visibleSymptoms = showAll ? sorted : sorted.slice(0, 10);

  const toggleSymptom = (key) => {
    onChange(prev => {
      if (prev[key]) { const { [key]: _, ...rest } = prev; return rest; }
      return { ...prev, [key]: 3 };
    });
  };

  const setSeverity = (key, val) => {
    onChange(prev => ({ ...prev, [key]: val }));
  };

  return (
    <div style={stepStyles.container}>
      <button style={stepStyles.backBtn} onClick={onBack}>← Back</button>
      <h2 style={stepStyles.stepTitle}>Any symptoms today?</h2>
      <p style={stepStyles.stepSubtitle}>Tap any that apply. Rate severity if you like.</p>

      {yesterdayKeys.length > 0 && (
        <div style={stepStyles.yesterdayBanner}>
          Yesterday's symptoms are shown first — tap to confirm if still present.
        </div>
      )}

      <div style={stepStyles.symptomList}>
        {visibleSymptoms.map(({ key, label, wasYesterday }) => (
          <div
            key={key}
            style={{
              ...stepStyles.symptomCard,
              ...(symptoms[key] ? stepStyles.symptomCardActive : {}),
              ...(wasYesterday && !symptoms[key] ? stepStyles.symptomCardYesterday : {}),
            }}
          >
            <button
              style={stepStyles.symptomRow}
              onClick={() => toggleSymptom(key)}
            >
              <span style={{
                ...stepStyles.checkbox,
                ...(symptoms[key] ? stepStyles.checkboxActive : {}),
              }}>
                {symptoms[key] ? '✓' : ''}
              </span>
              <span style={stepStyles.symptomLabel}>
                {label}
                {wasYesterday && !symptoms[key] && (
                  <span style={stepStyles.yesterdayTag}> · yesterday</span>
                )}
              </span>
            </button>
            {symptoms[key] && (
              <div style={stepStyles.severityRow}>
                {[1, 2, 3, 4, 5].map(n => (
                  <button
                    key={n}
                    style={{
                      ...stepStyles.severityBtn,
                      ...(symptoms[key] === n ? stepStyles.severityBtnActive : {}),
                    }}
                    onClick={() => setSeverity(key, n)}
                  >
                    {n}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {!showAll && sorted.length > 10 && (
        <button style={stepStyles.showMoreBtn} onClick={() => setShowAll(true)}>
          Show more symptoms ↓
        </button>
      )}

      <button style={stepStyles.primaryBtn} onClick={onNext}>
        {Object.keys(symptoms).length === 0 ? 'No symptoms today →' : `Continue with ${Object.keys(symptoms).length} symptom${Object.keys(symptoms).length !== 1 ? 's' : ''} →`}
      </button>
    </div>
  );
}

// ─── Step 4: Daily scores ───────────────────────────────────────────────────
const SCORE_ITEMS = [
  { key: 'energy', label: 'Energy', low: 'Exhausted', high: 'Full of energy' },
  { key: 'sleep',  label: 'Sleep last night', low: 'Terrible', high: 'Slept well' },
  { key: 'stress', label: 'Stress', low: 'Very stressed', high: 'Calm' },
];

function ScoresStep({ scores, onChange, onNext, onBack }) {
  return (
    <div style={stepStyles.container}>
      <button style={stepStyles.backBtn} onClick={onBack}>← Back</button>
      <h2 style={stepStyles.stepTitle}>A few quick scores</h2>
      <p style={stepStyles.stepSubtitle}>Slide to where you are today.</p>
      <div style={stepStyles.scoresContainer}>
        {SCORE_ITEMS.map(({ key, label, low, high }) => (
          <div key={key} style={stepStyles.scoreRow}>
            <div style={stepStyles.scoreLabelRow}>
              <span style={stepStyles.scoreLabel}>{label}</span>
              <span style={stepStyles.scoreValue}>{scores[key]}/10</span>
            </div>
            <div style={stepStyles.sliderHints}>
              <span style={stepStyles.sliderHint}>{low}</span>
              <span style={stepStyles.sliderHint}>{high}</span>
            </div>
            <input
              type="range" min="1" max="10"
              value={scores[key]}
              onChange={e => onChange(prev => ({ ...prev, [key]: parseInt(e.target.value) }))}
              style={stepStyles.slider}
            />
          </div>
        ))}
      </div>
      <button style={stepStyles.primaryBtn} onClick={onNext}>Continue →</button>
    </div>
  );
}

// ─── Step 5: Cycle check-in ─────────────────────────────────────────────────
const CYCLE_OPTIONS = [
  { key: 'period_light',  label: '🔴 Period (light)' },
  { key: 'period_heavy',  label: '🔴 Period (heavy)' },
  { key: 'spotting',      label: '🟠 Spotting' },
  { key: 'no_bleeding',   label: '⚪ No bleeding' },
  { key: 'irregular',     label: '❓ Irregular / not sure' },
  { key: 'no_periods',    label: '✓ No longer having periods' },
];

function CycleStep({ value, onChange, onNext, onBack }) {
  return (
    <div style={stepStyles.container}>
      <button style={stepStyles.backBtn} onClick={onBack}>← Back</button>
      <h2 style={stepStyles.stepTitle}>Cycle check-in</h2>
      <p style={stepStyles.stepSubtitle}>
        Helps the AI spot hormonal patterns. Skip if it doesn't apply.
      </p>
      <div style={stepStyles.cycleList}>
        {CYCLE_OPTIONS.map(({ key, label }) => (
          <button
            key={key}
            style={{
              ...stepStyles.cycleBtn,
              ...(value === key ? stepStyles.cycleBtnActive : {}),
            }}
            onClick={() => { onChange(key === value ? '' : key); }}
          >
            {label}
          </button>
        ))}
      </div>
      <button style={stepStyles.primaryBtn} onClick={onNext}>
        {value ? 'Continue →' : 'Skip →'}
      </button>
    </div>
  );
}

// ─── Step 6: Notes ──────────────────────────────────────────────────────────
function NotesStep({ value, onChange, onSubmit, onBack, saving }) {
  return (
    <div style={stepStyles.container}>
      <button style={stepStyles.backBtn} onClick={onBack}>← Back</button>
      <h2 style={stepStyles.stepTitle}>Anything else to note?</h2>
      <p style={stepStyles.stepSubtitle}>
        Optional. Just for you — these notes stay private and are never shared with AI analysis.
      </p>
      <textarea
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder="Anything you want to remember about today..."
        style={stepStyles.textarea}
        rows={4}
      />
      <button
        style={{
          ...stepStyles.primaryBtn,
          ...(saving ? stepStyles.primaryBtnDisabled : {}),
        }}
        onClick={onSubmit}
        disabled={saving}
      >
        {saving ? 'Saving...' : 'Save my log →'}
      </button>
    </div>
  );
}

// ─── Step 7: Reflection ─────────────────────────────────────────────────────
function ReflectionStep({ reflection, reflectionIsAI, loading, totalCount, onContinue }) {
  const milestoneLabels = { 3: 'Insights Unlocked', 7: 'One Week', 14: 'Two Weeks', 30: 'One Month' };
  const milestone = milestoneLabels[totalCount];

  return (
    <div style={stepStyles.reflectionContainer}>
      <div style={stepStyles.reflectionCard}>
        <div style={{
          fontFamily: serif, fontSize: '56px', fontWeight: '400', color: '#D4A5A5',
          lineHeight: '1', marginBottom: '8px',
          animation: 'fadeUp 0.4s ease forwards',
        }}>
          {totalCount}
        </div>

        {milestone && (
          <div style={stepStyles.milestoneBadge}>
            {milestone}
          </div>
        )}

        <div style={stepStyles.dayLabel}>Days logged</div>

        {loading ? (
          <div style={stepStyles.reflectionLoading}>
            <div style={stepStyles.loadingDot} />
            <div style={{ ...stepStyles.loadingDot, animationDelay: '0.15s' }} />
            <div style={{ ...stepStyles.loadingDot, animationDelay: '0.3s' }} />
          </div>
        ) : (
          <>
            <p style={{ ...stepStyles.reflectionText, ...(reflectionIsAI ? { marginBottom: '8px' } : {}) }}>{reflection}</p>
            {reflectionIsAI && <div style={{ marginBottom: '24px' }}><AiLabel centered /></div>}
          </>
        )}

        <button
          style={stepStyles.primaryBtn}
          onClick={onContinue}
          disabled={loading}
        >
          Done →
        </button>
      </div>
    </div>
  );
}

// ─── Step 5: Lifestyle tags ─────────────────────────────────────────────────
const LIFESTYLE_TAGS = [
  { key: 'alcohol',   emoji: '🍷', label: 'Alcohol' },
  { key: 'poor_sleep', emoji: '😴', label: 'Poor sleep' },
  { key: 'high_stress', emoji: '😓', label: 'High stress' },
  { key: 'exercise',  emoji: '🏃', label: 'Exercised' },
  { key: 'caffeine',  emoji: '☕', label: 'Lots of caffeine' },
  { key: 'travel',    emoji: '✈️', label: 'Travel / disruption' },
];

function LifestyleStep({ value, onChange, onNext, onBack }) {
  const toggle = (key) => {
    onChange(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  return (
    <div style={stepStyles.container}>
      <button style={stepStyles.backBtn} onClick={onBack}>← Back</button>
      <h2 style={stepStyles.stepTitle}>Anything notable today?</h2>
      <p style={stepStyles.stepSubtitle}>
        Tap anything that applies. This helps spot patterns over time. ✨ Pro feature preview.
      </p>
      <div style={lifestyleStyles.grid}>
        {LIFESTYLE_TAGS.map(({ key, emoji, label }) => {
          const active = value.includes(key);
          return (
            <button
              key={key}
              style={{
                ...lifestyleStyles.tag,
                ...(active ? lifestyleStyles.tagActive : {}),
              }}
              onClick={() => toggle(key)}
            >
              <span style={lifestyleStyles.tagEmoji}>{emoji}</span>
              <span style={lifestyleStyles.tagLabel}>{label}</span>
            </button>
          );
        })}
      </div>
      {value.length > 0 && (
        <p style={lifestyleStyles.hint}>
          Pro will show how these connect to your symptoms over time.
        </p>
      )}
      <button style={stepStyles.primaryBtn} onClick={onNext}>
        {value.length === 0 ? 'Nothing to note →' : `Continue with ${value.length} tag${value.length !== 1 ? 's' : ''} →`}
      </button>
    </div>
  );
}

const lifestyleStyles = {
  grid: {
    display: 'flex', flexWrap: 'wrap', gap: '8px',
    marginBottom: '24px',
  },
  tag: {
    display: 'flex', alignItems: 'center', gap: '8px',
    padding: '10px 16px', backgroundColor: 'white',
    border: '1.5px solid rgba(44,44,42,0.08)', borderRadius: '10px',
    cursor: 'pointer', fontFamily: sans,
    transition: 'border-color 0.15s, background-color 0.15s',
  },
  tagActive: {
    backgroundColor: '#fdf9f7', borderColor: '#D4A5A5',
  },
  tagEmoji: { fontSize: '18px' },
  tagLabel: { fontSize: '13px', fontWeight: '500', color: '#2C2C2A' },
  hint: {
    fontFamily: serif,
    fontSize: '15px', color: '#4a6070', fontStyle: 'italic',
    marginBottom: '20px', lineHeight: '1.6',
  },
};

// ─── Main SymptomLogger ─────────────────────────────────────────────────────
function SymptomLogger({ userEmail, sessionToken, journeyStage, onClose, onLogout, onGetTheApp }) {
  const [activeTab, setActiveTab] = useState('log');
  const [showWaitlist, setShowWaitlist] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showJourneyModal, setShowJourneyModal] = useState(false);
  const [localJourneyStage, setLocalJourneyStage] = useState(journeyStage);
  const [deleting, setDeleting] = useState(false);
  const [showPushPrompt, setShowPushPrompt] = useState(false);

  // Log data
  const [step, setStep] = useState(0); // 0=greeting, 1=mood, 2=symptoms, 3=scores, 4=cycle, 5=lifestyle, 6=notes, 7=reflection
  const [moodValue, setMoodValue] = useState(5);
  const [symptoms, setSymptoms] = useState({});
  const [scores, setScores] = useState({ energy: 5, sleep: 5, stress: 5 });
  const [cyclePhase, setCyclePhase] = useState('');
  const [notes, setNotes] = useState('');
  const [lifestyleTags, setLifestyleTags] = useState([]);
  const [saving, setSaving] = useState(false);
  const [isEditingYesterday, setIsEditingYesterday] = useState(false);
  const [editYesterdaySuccess, setEditYesterdaySuccess] = useState(false);
  const [error, setError] = useState('');

  // User context
  const [totalCount, setTotalCount] = useState(0);
  const [todayLogged, setTodayLogged] = useState(false);
  const [yesterdayLog, setYesterdayLog] = useState(null);
  const [initialising, setInitialising] = useState(true);

  // AI content
  const [greeting, setGreeting] = useState('');
  const [greetingIsAI, setGreetingIsAI] = useState(false);
  const [greetingLoading, setGreetingLoading] = useState(true);
  const [reflection, setReflection] = useState('');
  const [reflectionIsAI, setReflectionIsAI] = useState(false);
  const [reflectionLoading, setReflectionLoading] = useState(false);

  const authFetch = (url, options = {}) => fetch(url, {
    ...options,
    headers: { ...options.headers, 'Authorization': `Bearer ${sessionToken}` },
  });

  const handleSessionExpired = () => {
    // App.handleLogout signs out of Supabase and clears local profile flags.
    if (onLogout) onLogout();
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      const res = await authFetch('/api/delete-account', { method: 'POST' });
      if (res.ok) {
        try { localStorage.removeItem('apptnote_user_email'); } catch (e) { /* ignore */ }
        if (onLogout) onLogout();
      } else {
        alert('Something went wrong. Please try again or email privacy@apptnote.com');
      }
    } catch (err) {
      alert('Something went wrong. Please try again or email privacy@apptnote.com');
    } finally {
      setDeleting(false);
    }
  };

  // Load user context on mount
  useEffect(() => {
    loadUserContext();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadUserContext = async () => {
    try {
      const res = await authFetch('/api/log-symptoms');
      if (res.status === 401) { handleSessionExpired(); return; }
      const data = await res.json();
      setTotalCount(data.totalCount ?? 0);
      setTodayLogged(data.todayLogged ?? false);
      setYesterdayLog(data.yesterdayLog ?? null);
      setInitialising(false);
      // Fetch greeting after we have context
      fetchGreeting(data.totalCount ?? 0, data.yesterdayLog ?? null);
    } catch (err) {
      console.error('Failed to load user context');
      setInitialising(false);
      setGreetingLoading(false);
    }
  };

  const fetchGreeting = async (count, ydayLog) => {
    setGreetingLoading(true);
    try {
      const res = await authFetch('/api/greeting', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          journey_stage: journeyStage,
          total_count: count,
          yesterday_log: ydayLog
            ? { symptoms: ydayLog.symptoms, daily_scores: ydayLog.daily_scores }
            : null,
        }),
      });
      const data = await res.json();
      setGreeting(data.greeting || "Welcome back. Let's log how today has been.");
      setGreetingIsAI(!!data.greeting && !data.fallback);
    } catch (err) {
      setGreeting("Welcome back. Let's log how today has been.");
      setGreetingIsAI(false);
    } finally {
      setGreetingLoading(false);
    }
  };

  const goTo = (nextStep) => setStep(nextStep);

  const goBack = (prevStep) => setStep(prevStep);

    const handleSubmit = async () => {
    setSaving(true);
    setError('');

    const today = new Date().toISOString().split('T')[0];
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayStr = yesterday.toISOString().split('T')[0];

    const daily_scores = { mood: moodValue, ...scores };

    // Choose method and date based on edit mode
    const method = isEditingYesterday ? 'PATCH' : 'POST';
    const log_date = isEditingYesterday ? yesterdayStr : today;

    try {
      const res = await authFetch('/api/log-symptoms', {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          log_date,
          symptoms,
          daily_scores,
          cycle_phase: cyclePhase || null,
          notes: notes.trim() || null,
          lifestyle_tags: lifestyleTags.length > 0 ? lifestyleTags : null,
        }),
      });

      if (res.status === 401) { handleSessionExpired(); return; }

      if (res.ok) {
        if (isEditingYesterday) {
          // Quiet success — no reflection, no count change
          setIsEditingYesterday(false);
          setEditYesterdaySuccess(true);
          setMoodValue(5);
          setSymptoms({});
          setScores({ energy: 5, sleep: 5, stress: 5 });
          setCyclePhase('');
          setNotes('');
          setLifestyleTags([]);
          goTo(0);
        } else {
          const newCount = totalCount + (todayLogged ? 0 : 1);
          setTotalCount(newCount);
          if (!localStorage.getItem('apptnote_push_prompted')) {
            setShowPushPrompt(true);
          }
          goTo(7);
          fetchReflection(newCount, daily_scores, symptoms);
        }
      } else {
        const data = await res.json();
        setError(data.error || 'Failed to save. Please try again.');
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const fetchReflection = async (count, today_scores, today_symptoms) => {
    setReflectionLoading(true);
    try {
      const res = await authFetch('/api/reflection', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          journey_stage: journeyStage,
          total_count: count,
          today_symptoms,
          today_scores,
          yesterday_symptoms: yesterdayLog?.symptoms ?? null,
          yesterday_scores: yesterdayLog?.daily_scores ?? null,
        }),
      });
      const data = await res.json();
      setReflection(data.reflection || "Today is logged. Every entry builds your picture.");
      setReflectionIsAI(!!data.reflection && !data.fallback);
    } catch (err) {
      setReflection("Today is logged. Every entry builds your picture.");
      setReflectionIsAI(false);
    } finally {
      setReflectionLoading(false);
    }
  };

  const handleReflectionContinue = () => {
    // Reset form state but mark today as logged — show done state, not greeting
    setMoodValue(5);
    setSymptoms({});
    setScores({ energy: 5, sleep: 5, stress: 5 });
    setCyclePhase('');
    setNotes('');
    setLifestyleTags([]);
    setLifestyleTags([]);
    setTodayLogged(true);
    setStep(0); // GreetingStep handles todayLogged=true by showing done state
  };
    const startEditYesterday = () => {
    if (!yesterdayLog) return;
    // Pre-populate form from yesterday's saved data
    setMoodValue(yesterdayLog.daily_scores?.mood ?? 5);
    setScores({
      energy: yesterdayLog.daily_scores?.energy ?? 5,
      sleep:  yesterdayLog.daily_scores?.sleep  ?? 5,
      stress: yesterdayLog.daily_scores?.stress ?? 5,
    });
    setSymptoms(yesterdayLog.symptoms ?? {});
    setCyclePhase(yesterdayLog.cycle_phase ?? '');
    setLifestyleTags(yesterdayLog.lifestyle_tags ?? []);
    setNotes(yesterdayLog.notes ?? '');
    setIsEditingYesterday(true);
    setEditYesterdaySuccess(false);
    goTo(1); // start at mood step, pre-filled
  };

  return (
    <div style={styles.container}>
      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes slideIn {
          from { opacity: 0; transform: translateX(40px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @keyframes slideOut {
          from { opacity: 1; transform: translateX(0); }
          to   { opacity: 0; transform: translateX(-40px); }
        }
        @keyframes blink {
          0%, 80%, 100% { opacity: 0.2; transform: scale(0.8); }
          40%            { opacity: 1; transform: scale(1); }
        }
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50%       { transform: scale(1.06); }
        }
      `}</style>

      <div style={styles.inner}>

        {/* Header */}
        <div style={styles.header}>
          <span style={styles.logoAppt}>Appt<span style={styles.logoNote}>Note</span></span>
          <div style={styles.headerActions}>
            {onClose && (
              <button style={styles.headerBtn} onClick={onClose}>Back</button>
            )}
            {onLogout && (
              <button style={{ ...styles.headerBtn, color: '#D4A5A5' }} onClick={onLogout}>Sign out</button>
            )}
          </div>
        </div>

        {(step === 0 || step === 7) && <ClosingNotice forUsers />}

        {/* Tabs — only show when not mid-flow */}
        {(step === 0 || step === 7) && (
          <div style={styles.tabs}>
            <button
              style={activeTab === 'log' ? { ...styles.tab, ...styles.tabActive } : styles.tab}
              onClick={() => setActiveTab('log')}
            >
              Today's Log
            </button>
            <button
              style={activeTab === 'calendar' ? { ...styles.tab, ...styles.tabActive } : styles.tab}
              onClick={() => setActiveTab('calendar')}
            >
              Calendar
            </button>
            <button
              style={activeTab === 'report' ? { ...styles.tab, ...styles.tabActive } : { ...styles.tab, ...styles.tabReport }}
              onClick={() => setActiveTab('report')}
            >
              Insights
            </button>
          </div>
        )}

        <InstallPrompt />

        {/* ── Log tab ── */}
        {activeTab === 'log' && initialising && (
          <div style={{ padding: '60px 0', textAlign: 'center' }}>
            <div style={{ display: 'inline-flex', gap: '8px', alignItems: 'center' }}>
              <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#D4A5A5', animation: 'blink 1.2s infinite ease-in-out' }} />
              <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#D4A5A5', animation: 'blink 1.2s infinite ease-in-out', animationDelay: '0.15s' }} />
              <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#D4A5A5', animation: 'blink 1.2s infinite ease-in-out', animationDelay: '0.3s' }} />
            </div>
          </div>
        )}

        {activeTab === 'log' && !initialising && (
          <div style={styles.stepWrapper}>

          {step === 0 && (
              <>
                {editYesterdaySuccess && (
                  <div style={{
                    backgroundColor: '#f0f7f2', border: '1px solid #7A9E87',
                    borderRadius: '12px', padding: '12px 16px',
                    marginBottom: '16px', fontSize: '14px',
                    color: '#3a6b4a', textAlign: 'center',
                  }}>
                    Yesterday updated
                  </div>
                )}
                <GreetingStep
                  greeting={greeting}
                  greetingIsAI={greetingIsAI}
                  loading={greetingLoading || initialising}
                  onNext={() => { setEditYesterdaySuccess(false); goTo(1); }}
                  totalCount={totalCount}
                  todayLogged={todayLogged}
                  onEditYesterday={yesterdayLog ? startEditYesterday : null}
                />
              </>
            )}

            {step === 1 && (
              <MoodStep
                value={moodValue}
                onChange={setMoodValue}
                onNext={() => goTo(2)}
                onBack={() => goBack(0)}
              />
            )}

            {step === 2 && (
              <SymptomsStep
                symptoms={symptoms}
                onChange={setSymptoms}
                onNext={() => goTo(3)}
                onBack={() => goBack(1)}
                journeyStage={journeyStage}
                yesterdaySymptoms={yesterdayLog?.symptoms}
              />
            )}

            {step === 3 && (
              <ScoresStep
                scores={scores}
                onChange={setScores}
                onNext={() => goTo(4)}
                onBack={() => goBack(2)}
              />
            )}

            {step === 4 && (
              <CycleStep
                value={cyclePhase}
                onChange={setCyclePhase}
                onNext={() => goTo(5)}
                onBack={() => goBack(3)}
              />
            )}

            {step === 5 && (
              <LifestyleStep
                value={lifestyleTags}
                onChange={setLifestyleTags}
                onNext={() => goTo(6)}
                onBack={() => goBack(4)}
              />
            )}

            {step === 6 && (
              <>
                <NotesStep
                  value={notes}
                  onChange={setNotes}
                  onSubmit={handleSubmit}
                  onBack={() => goBack(5)}
                  saving={saving}
                />
                {error && <p style={styles.error}>{error}</p>}
                {showPushPrompt && (
                  <PushPermissionPrompt
                    userEmail={userEmail}
                    onDismiss={() => setShowPushPrompt(false)}
                  />
                )}
              </>
            )}

            {step === 7 && (
              <ReflectionStep
                reflection={reflection}
                reflectionIsAI={reflectionIsAI}
                loading={reflectionLoading}
                totalCount={totalCount}
                onContinue={handleReflectionContinue}
              />
            )}

          </div>
          )}
     
        {/* ── Calendar tab ── */}
        {activeTab === 'calendar' && (
          <SymptomCalendar
            token={sessionToken}
            onJoinWaitlist={() => setShowWaitlist(true)}
          />
        )}

        {/* ── Report tab ── */}
        {activeTab === 'report' && (
          <ReportPage
            userEmail={userEmail}
            token={sessionToken}
            journeyStage={journeyStage}
            onBack={() => setActiveTab('log')}
          />
        )}

      </div>

      {/* Footer */}
      {activeTab === 'log' && (step === 0 || step === 7) && (
        <div style={styles.footer}>

          {/* Journey stage prompt — only for users with no stage set */}
          {!localJourneyStage && totalCount >= 1 && (
            <div style={styles.journeyPrompt}>
              <p style={styles.journeyPromptText}>
                Tell us where you are in your journey so we can personalise your experience
              </p>
              <button style={styles.journeyPromptBtn} onClick={() => setShowJourneyModal(true)}>
                Set my journey stage →
              </button>
            </div>
          )}

          {/* Report tab nudge — after 3 logs */}
          {totalCount >= 3 && (
            <div style={styles.reportNudge} onClick={() => setActiveTab('report')}>
              <span style={{ ...styles.reportNudgeIcon, fontFamily: serif, fontSize: '18px', color: '#D4A5A5', fontWeight: '400' }}>↗</span>
              <div>
                <div style={styles.reportNudgeTitle}>Your trends are building</div>
                <div style={styles.reportNudgeText}>Tap Insights to see your patterns →</div>
              </div>
            </div>
          )}

          {/* Get the app nudge */}
          <div style={styles.appNudge}>
            <span style={{ ...styles.appNudgeIcon, fontSize: '16px', color: '#7A9E87', fontWeight: '600', fontFamily: sans }}>⊕</span>
            <div style={styles.appNudgeContent}>
              <div style={styles.appNudgeTitle}>Get daily reminders</div>
              <div style={styles.appNudgeText}>Add ApptNote to your home screen for push notifications</div>
            </div>
            <button style={styles.appNudgeBtn} onClick={onGetTheApp}>
              Get the app
            </button>
          </div>

          {/* Delete account */}
          {!showDeleteConfirm ? (
            <p style={styles.footerText}>
              Want to export your data? Email{' '}
              <a href="mailto:privacy@apptnote.com" style={styles.footerLink}>
                privacy@apptnote.com
              </a>
              {' · '}
              <button style={styles.deleteLink} onClick={() => setShowDeleteConfirm(true)}>
                Delete my account
              </button>
            </p>
          ) : (
            <div style={styles.deleteConfirm}>
              <p style={styles.deleteConfirmText}>
                This will permanently delete all your symptom logs, scores and account data. This cannot be undone.
              </p>
              <div style={styles.deleteConfirmButtons}>
                <button style={styles.deleteConfirmCancel} onClick={() => setShowDeleteConfirm(false)} disabled={deleting}>
                  Cancel
                </button>
                <button style={styles.deleteConfirmGo} onClick={handleDeleteAccount} disabled={deleting}>
                  {deleting ? 'Deleting...' : 'Yes, delete everything'}
                </button>
              </div>
            </div>
          )}

          <p style={styles.disclaimer}>
            ApptNote is not a medical service. Insights help you communicate with your doctor,
            not replace medical advice. In an emergency, call 112 (Ireland/EU) or 911 (US).
          </p>
        </div>
      )}

      {showWaitlist && <WaitlistModal onClose={() => setShowWaitlist(false)} context="pro" />}
      {showJourneyModal && (
        <JourneyStageModal
          userEmail={userEmail}
          token={sessionToken}
          onClose={() => setShowJourneyModal(false)}
          onSaved={(stage) => setLocalJourneyStage(stage)}
        />
      )}
    </div>
  );
}

// ─── Styles ─────────────────────────────────────────────────────────────────
const styles = {
  container: {
    minHeight: '100vh', backgroundColor: '#FAF7F2',
    fontFamily: sans,
  },
  inner: { maxWidth: '560px', margin: '0 auto', padding: '0 24px 60px' },
  header: {
    padding: '28px 0 20px', borderBottom: '1px solid rgba(44,44,42,0.08)',
    marginBottom: '24px', display: 'flex', alignItems: 'center',
  },
  logoAppt: { fontSize: '22px', fontWeight: '700', color: '#2C2C2A', fontFamily: sans },
  logoNote: { fontSize: '22px', fontWeight: '700', color: '#D4A5A5', fontFamily: sans },
  headerActions: { marginLeft: 'auto', display: 'flex', gap: '12px' },
  headerBtn: {
    background: 'none', border: 'none', color: '#6c8396',
    fontSize: '13px', fontWeight: '500', cursor: 'pointer', fontFamily: sans,
    padding: '6px 0', letterSpacing: '0.01em',
  },
  tabs: {
    display: 'flex', gap: '6px', marginBottom: '32px',
    backgroundColor: 'rgba(44,44,42,0.04)', padding: '5px', borderRadius: '12px',
  },
  tab: {
    flex: 1, padding: '10px 8px', background: 'transparent', border: 'none',
    borderRadius: '8px', fontSize: '13px', fontWeight: '600', color: '#6c8396',
    cursor: 'pointer', fontFamily: sans,
  },
  tabActive: { backgroundColor: 'white', color: '#2C2C2A', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  tabReport: { color: '#D4A5A5', fontWeight: '700' },
  tabDot: {
    display: 'inline-block', width: '6px', height: '6px',
    borderRadius: '50%', backgroundColor: '#D4A5A5',
    marginLeft: '5px', verticalAlign: 'middle',
  },
  stepWrapper: { animation: 'slideIn 0.32s cubic-bezier(0.4,0,0.2,1) forwards' },
  error: { color: '#c44444', fontSize: '14px', textAlign: 'center', marginTop: '12px' },
  footer: {
    maxWidth: '560px', margin: '0 auto', padding: '24px 24px 48px',
    textAlign: 'center', borderTop: '1px solid rgba(44,44,42,0.08)',
  },
  footerText: { fontSize: '13px', color: '#6c8396', marginBottom: '8px' },
  footerLink: { color: '#7A9E87', textDecoration: 'underline' },
  disclaimer: { fontSize: '12px', color: '#6c8396', lineHeight: '1.6', maxWidth: '480px', margin: '0 auto' },
  journeyPrompt: {
    backgroundColor: 'white', border: '1px solid rgba(122,158,135,0.3)',
    borderLeft: '3px solid #7A9E87',
    borderRadius: '8px', padding: '14px 16px', marginBottom: '12px', textAlign: 'left',
  },
  journeyPromptText: { fontSize: '14px', color: '#2C2C2A', margin: '0 0 10px', lineHeight: '1.5', fontWeight: '300' },
  journeyPromptBtn: {
    background: 'none', border: 'none', color: '#7A9E87', fontSize: '14px',
    fontWeight: '600', cursor: 'pointer', padding: '0', fontFamily: sans,
  },
  reportNudge: {
    display: 'flex', alignItems: 'center', gap: '12px',
    backgroundColor: 'white', border: '1px solid rgba(212,165,165,0.3)',
    borderLeft: '3px solid #D4A5A5',
    borderRadius: '8px', padding: '14px 16px', marginBottom: '12px',
    cursor: 'pointer',
  },
  reportNudgeIcon: { fontSize: '20px', flexShrink: 0 },
  reportNudgeTitle: { fontSize: '14px', fontWeight: '600', color: '#2C2C2A', marginBottom: '2px' },
  reportNudgeText: { fontSize: '13px', color: '#6c8396', fontWeight: '300' },
  appNudge: {
    display: 'flex', alignItems: 'center', gap: '12px',
    backgroundColor: 'white', border: '1px solid rgba(44,44,42,0.08)',
    borderRadius: '10px', padding: '14px 16px', marginBottom: '16px',
  },
  appNudgeIcon: { fontSize: '20px', flexShrink: 0 },
  appNudgeContent: { flex: 1 },
  appNudgeTitle: { fontSize: '14px', fontWeight: '600', color: '#2C2C2A', marginBottom: '2px' },
  appNudgeText: { fontSize: '12px', color: '#6c8396', fontWeight: '300' },
  appNudgeBtn: {
    backgroundColor: '#7A9E87', color: 'white', border: 'none',
    borderRadius: '8px', padding: '8px 14px', fontSize: '13px',
    fontWeight: '600', cursor: 'pointer', fontFamily: sans, flexShrink: 0,
  },
  deleteLink: {
    background: 'none', border: 'none', color: '#6c8396',
    fontSize: '13px', textDecoration: 'underline', cursor: 'pointer',
    fontFamily: sans, padding: 0,
  },
  deleteConfirm: {
    backgroundColor: 'white', border: '1px solid rgba(212,165,165,0.4)',
    borderRadius: '10px', padding: '16px 20px', marginBottom: '12px',
  },
  deleteConfirmText: { fontSize: '13px', color: '#2C2C2A', lineHeight: '1.6', margin: '0 0 14px', fontWeight: '300' },
  deleteConfirmButtons: { display: 'flex', gap: '10px' },
  deleteConfirmCancel: {
    flex: 1, padding: '10px', backgroundColor: 'white',
    border: '1.5px solid rgba(44,44,42,0.12)', borderRadius: '8px',
    fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: sans, color: '#4a6070',
  },
  deleteConfirmGo: {
    flex: 1, padding: '10px', backgroundColor: '#D4A5A5',
    border: 'none', borderRadius: '8px', fontSize: '13px',
    fontWeight: '600', cursor: 'pointer', fontFamily: sans, color: 'white',
  },
};

const stepStyles = {
  container: {
    paddingTop: '8px', paddingBottom: '40px',
  },
  backBtn: {
    background: 'none', border: 'none', color: '#6c8396', fontSize: '14px',
    cursor: 'pointer', fontFamily: sans, padding: '0 0 24px', display: 'block',
    fontWeight: '500',
  },
  stepBadge: {
    display: 'inline-block',
    fontSize: '11px', fontWeight: '600', letterSpacing: '0.12em',
    textTransform: 'uppercase', color: '#7A9E87', marginBottom: '16px',
    padding: '5px 14px', border: '1px solid rgba(122,158,135,0.3)',
    borderRadius: '100px',
  },
  stepTitle: {
    fontFamily: serif,
    fontSize: '30px', fontWeight: '400', color: '#1a1a18',
    marginBottom: '8px', lineHeight: '1.2', letterSpacing: '-0.01em',
  },
  stepSubtitle: {
    fontSize: '15px', color: '#4a6070', lineHeight: '1.65',
    marginBottom: '28px', fontWeight: '300',
  },

  // Greeting
  aiLabel: {
    display: 'flex', alignItems: 'center', gap: '6px',
    fontFamily: sans, fontSize: '12px', color: '#6c8396',
    letterSpacing: '0.02em', marginBottom: '8px',
  },
  greetingText: {
    fontFamily: serif,
    fontSize: '22px', color: '#1a1a18', lineHeight: '1.6',
    fontWeight: '400', marginBottom: '8px', fontStyle: 'italic',
    animation: 'fadeUp 0.5s ease 0.1s forwards', opacity: 0,
  },
  greetingLoading: {
    display: 'flex', gap: '8px', alignItems: 'center',
    height: '60px', paddingLeft: '4px',
  },
  loadingDot: {
    width: '8px', height: '8px', borderRadius: '50%',
    backgroundColor: '#D4A5A5',
    animation: 'blink 1.2s infinite ease-in-out',
  },
  alreadyLoggedBanner: {
    backgroundColor: 'white', border: '1px solid rgba(122,158,135,0.25)',
    borderLeft: '3px solid #7A9E87',
    borderRadius: '8px', padding: '12px 16px',
    fontSize: '14px', color: '#4a6070', marginBottom: '8px', fontWeight: '300',
  },

  // Mood — word pills instead of emoji buttons
  moodGrid: {
    display: 'flex', gap: '10px', marginBottom: '32px', flexWrap: 'wrap',
  },
  moodBtn: {
    flex: '1 1 80px', display: 'flex', flexDirection: 'column', alignItems: 'center',
    gap: '4px', padding: '16px 8px', backgroundColor: 'white',
    border: '1.5px solid rgba(44,44,42,0.10)', borderRadius: '12px',
    cursor: 'pointer', fontFamily: sans,
    transition: 'border-color 0.15s, background-color 0.15s',
  },
  moodBtnActive: { backgroundColor: '#fdf5f5', borderColor: '#D4A5A5' },
  moodEmoji: { fontSize: '24px' },
  moodLabel: { fontSize: '12px', color: '#4a6070', fontWeight: '600' },

  // Symptoms
  yesterdayBanner: {
    backgroundColor: 'white', border: '1px solid rgba(122,158,135,0.25)',
    borderLeft: '3px solid #7A9E87',
    borderRadius: '8px', padding: '10px 14px',
    fontSize: '13px', color: '#2C2C2A', marginBottom: '16px', fontWeight: '300',
  },
  symptomList: { display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '16px' },
  symptomCard: {
    backgroundColor: 'white', border: '1.5px solid rgba(44,44,42,0.08)',
    borderRadius: '10px', overflow: 'hidden',
    transition: 'border-color 0.15s',
  },
  symptomCardActive: { borderColor: '#D4A5A5', backgroundColor: '#fdf9f7' },
  symptomCardYesterday: { borderColor: 'rgba(122,158,135,0.35)', backgroundColor: '#f8fbf9' },
  symptomRow: {
    display: 'flex', alignItems: 'center', gap: '12px',
    padding: '13px 16px', background: 'none', border: 'none',
    cursor: 'pointer', width: '100%', textAlign: 'left', fontFamily: sans,
  },
  checkbox: {
    width: '20px', height: '20px', borderRadius: '5px',
    border: '1.5px solid rgba(44,44,42,0.15)', display: 'flex', alignItems: 'center',
    justifyContent: 'center', fontSize: '11px', color: '#7A9E87',
    fontWeight: '700', backgroundColor: 'white', flexShrink: 0,
  },
  checkboxActive: { backgroundColor: '#7A9E87', borderColor: '#7A9E87', color: 'white' },
  symptomLabel: { fontSize: '14px', color: '#2C2C2A', fontWeight: '400' },
  yesterdayTag: { fontSize: '11px', color: '#7A9E87', fontWeight: '600' },
  severityRow: { display: 'flex', gap: '6px', padding: '0 16px 12px' },
  severityBtn: {
    flex: 1, padding: '7px', border: '1.5px solid rgba(44,44,42,0.08)', borderRadius: '7px',
    backgroundColor: 'white', cursor: 'pointer', fontSize: '12px',
    fontWeight: '600', color: '#4a6070', fontFamily: sans,
  },
  severityBtnActive: { backgroundColor: '#D4A5A5', borderColor: '#D4A5A5', color: 'white' },
  showMoreBtn: {
    width: '100%', padding: '12px', background: 'none',
    border: '1.5px dashed rgba(44,44,42,0.12)', borderRadius: '10px',
    fontSize: '13px', color: '#6c8396', cursor: 'pointer',
    fontFamily: sans, marginBottom: '16px',
  },

  // Scores
  scoresContainer: { display: 'flex', flexDirection: 'column', gap: '24px', marginBottom: '32px' },
  scoreRow: {},
  scoreLabelRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: '6px',
  },
  scoreLabel: { fontSize: '15px', fontWeight: '500', color: '#2C2C2A' },
  scoreValue: {
    fontFamily: serif, fontSize: '18px', fontWeight: '400', color: '#7A9E87',
  },
  sliderHints: { display: 'flex', justifyContent: 'space-between', marginBottom: '4px' },
  sliderHint: { fontSize: '11px', color: '#6c8396' },
  slider: { width: '100%', accentColor: '#7A9E87' },

  // Cycle
  cycleList: { display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '24px' },
  cycleBtn: {
    padding: '13px 18px', backgroundColor: 'white',
    border: '1.5px solid rgba(44,44,42,0.08)', borderRadius: '10px',
    fontSize: '14px', color: '#2C2C2A', cursor: 'pointer',
    fontFamily: sans, textAlign: 'left',
    transition: 'border-color 0.15s',
  },
  cycleBtnActive: { backgroundColor: '#fdf9f7', borderColor: '#D4A5A5', fontWeight: '600' },

  // Notes
  textarea: {
    width: '100%', padding: '14px 16px', fontSize: '15px',
    border: '1.5px solid rgba(44,44,42,0.10)', borderRadius: '10px',
    backgroundColor: 'white', fontFamily: sans, color: '#2C2C2A',
    resize: 'vertical', boxSizing: 'border-box', marginBottom: '20px',
    lineHeight: '1.6', fontWeight: '300',
  },

  // Reflection
  reflectionContainer: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    minHeight: '60vh',
  },
  reflectionCard: {
    textAlign: 'center', padding: '40px 24px',
    maxWidth: '440px', width: '100%',
    animation: 'fadeUp 0.4s ease forwards',
  },
  milestoneBadge: {
    display: 'inline-block', backgroundColor: '#7A9E87', color: 'white',
    padding: '6px 18px', borderRadius: '100px', fontSize: '12px', fontWeight: '600',
    marginBottom: '16px', letterSpacing: '0.08em', textTransform: 'uppercase',
    animation: 'pulse 0.6s ease-in-out 0.4s 2',
  },
  dayLabel: {
    fontSize: '11px', fontWeight: '600', color: '#D4A5A5',
    textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: '20px',
  },
  reflectionLoading: {
    display: 'flex', gap: '8px', justifyContent: 'center',
    alignItems: 'center', height: '60px',
  },
  reflectionText: {
    fontFamily: serif,
    fontSize: '20px', color: '#1a1a18', lineHeight: '1.7',
    marginBottom: '32px', fontWeight: '400', fontStyle: 'italic',
    animation: 'fadeUp 0.5s ease 0.2s forwards', opacity: 0,
  },

  // Shared
  primaryBtn: {
    width: '100%', padding: '16px', backgroundColor: '#2C2C2A',
    color: 'white', border: 'none', borderRadius: '10px',
    fontSize: '15px', fontWeight: '600', cursor: 'pointer',
    fontFamily: sans,
  },
  primaryBtnDisabled: { backgroundColor: '#a0a09e', cursor: 'not-allowed' },
};

export default SymptomLogger;